import { useState, useEffect } from 'react';
import { Card, Typography, Table, Alert, Space, Tag, Steps, Select, Button, Flex, message } from 'antd';
import getEnv from '../../env';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useNodeDomain } from '../../hooks/use_node_domain';

const { Title, Text, Paragraph } = Typography;

// The Cloud DNS name servers every zone in this project has been assigned so
// far. Google picks a set per zone at creation time and it is not guaranteed
// to be this one, which is why step 1 is "tell us the domain" — the four are
// confirmed when the zone is created, not chosen by us.
const NAME_SERVERS = [
    'ns-cloud-b1.googledomains.com',
    'ns-cloud-b2.googledomains.com',
    'ns-cloud-b3.googledomains.com',
    'ns-cloud-b4.googledomains.com',
];

/**
 * Superadmin-only: what this node answers on, and how to put it behind a
 * domain the node's own organisation owns (warmdatabase.com serving the
 * warmdata node, say).
 *
 * The flow described here is delegation, not per-record instructions: the
 * domain's name servers move to our Cloud DNS, and everything downstream —
 * records, TLS certificate, the node's route, and the Keycloak client's
 * redirect URIs — is configured on our side. That is a deliberate division.
 * Only the first of those is DNS; the rest cannot be done from a registrar at
 * all, so an admin who follows record-level instructions would end up with a
 * domain that resolves and still fails the TLS handshake.
 *
 * The setup itself is not self-service. What is: choosing which of the
 * node's live domains invite links use (node_settings.custom_domain). The
 * picker only offers hosts the backend's deploy config says this node is
 * served on (WEB_APP_EXTRA_HOSTS), so an admin can't point invites at a
 * domain that doesn't answer yet — see routers/node_settings.py.
 */
// Which of the node's live domains the /claim/<token> links in invitations
// use. Only the token is stored per invite and the host is added each time a
// link is built, so a change reaches every link copied or emailed from now
// on; links already sent keep working because every option serves this node.
function InviteLinkDomain() {
    const { customDomain, defaultHost, options, loading, save } = useNodeDomain();
    const current = customDomain || defaultHost;
    const [selected, setSelected] = useState(current);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setSelected(current); }, [current]);

    const onSave = () => {
        setSaving(true);
        save(selected)
            .then(() => message.success('Invite links updated'))
            .catch(err => message.error(err?.message || 'Could not change the invite link domain'))
            .finally(() => setSaving(false));
    };

    return (
        <Card loading={loading}>
            <Title level={5} style={{ marginTop: 0 }}>Invite links</Title>
            {options.length > 1 ? (
                <Space direction="vertical" size="small" style={{ display: 'flex' }}>
                    <Text type="secondary">
                        The address people land on when they open an invitation to claim their profile.
                    </Text>
                    <Flex gap={8} wrap>
                        <Select
                            style={{ minWidth: 260 }}
                            value={selected}
                            onChange={setSelected}
                            options={options.map(host => ({
                                value: host,
                                label: host === defaultHost ? `${host} (default)` : host,
                            }))}
                        />
                        <Button type="primary" onClick={onSave} loading={saving} disabled={selected === current}>
                            Save
                        </Button>
                    </Flex>
                    <Text type="secondary">
                        New links look like <Text code>{`https://${selected}/claim/…`}</Text>. Links
                        already sent keep working.
                    </Text>
                </Space>
            ) : (
                <Text type="secondary">
                    Invitations link people to <Text code>{current}</Text>. Once your own domain is
                    live, you can switch them to it here.
                </Text>
            )}
        </Card>
    );
}

function NodeDns() {
    const { nodeId } = useNodeInfo();
    const appHost = window.location.host;
    const apiUrl = getEnv('REACT_APP_API_URL') || '';
    const apiHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const authUrl = getEnv('REACT_APP_KEYCLOAK_URL') || '';
    const authHost = authUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const realm = getEnv('REACT_APP_KEYCLOAK_REALM') || '';

    const addresses = [
        { key: 'app', what: 'Web app', host: appHost, note: 'What people type to reach this node' },
        { key: 'api', what: 'API', host: apiHost, note: 'Called by the web app, the CLI and agents over MCP' },
        { key: 'auth', what: 'Sign-in', host: authHost, note: realm ? `Keycloak realm "${realm}"` : 'Keycloak' },
    ].filter(row => row.host);

    return (
        <Space direction="vertical" size="middle" style={{ display: 'flex', marginTop: 16 }}>
            <Card>
                <Title level={5} style={{ marginTop: 0 }}>This node's addresses</Title>
                <Text type="secondary">
                    {nodeId ? <>Node <Tag style={{ marginInlineStart: 4 }}>{nodeId}</Tag> is served on:</> : 'This node is served on:'}
                </Text>
                <Table
                    style={{ marginTop: 12 }}
                    size="small"
                    pagination={false}
                    dataSource={addresses}
                    columns={[
                        { title: '', dataIndex: 'what', width: 100 },
                        {
                            title: 'Hostname',
                            dataIndex: 'host',
                            render: host => <Text copyable code>{host}</Text>,
                        },
                        {
                            title: '',
                            dataIndex: 'note',
                            responsive: ['md'],
                            render: note => <Text type="secondary">{note}</Text>,
                        },
                    ]}
                />
            </Card>

            <InviteLinkDomain />

            <Card>
                <Title level={5} style={{ marginTop: 0 }}>Use your own domain</Title>
                <Paragraph type="secondary">
                    This node can also be served on a domain your organisation owns, instead
                    of only on <Text code>{appHost}</Text>. One step is yours; the Vellby team
                    does the rest.
                </Paragraph>
                <Steps
                    direction="vertical"
                    size="small"
                    current={-1}
                    items={[
                        {
                            title: 'Tell the Vellby team your domain',
                            description: 'We create a DNS zone for it and confirm the four name servers to use — normally the ones below.',
                        },
                        {
                            title: 'Point the domain\'s name servers at them',
                            description: (
                                <>
                                    <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                                        At your registrar (GoDaddy, Namecheap, and so on), replace the
                                        domain's name servers with these:
                                    </Paragraph>
                                    <Text copyable={{ text: NAME_SERVERS.join('\n') }}>
                                        {NAME_SERVERS.map(ns => (
                                            <div key={ns}><Text code>{ns}</Text></div>
                                        ))}
                                    </Text>
                                </>
                            ),
                        },
                        {
                            title: 'We do the rest',
                            description: 'Records, TLS certificate, routing to this node, and allowing sign-in to return to the new domain. We tell you when it is live.',
                        },
                    ]}
                />
                <Alert
                    style={{ marginTop: 8 }}
                    type="warning"
                    showIcon
                    message="Changing name servers moves the whole domain"
                    description={
                        <>
                            Every record for the domain starts being answered by us, not your
                            registrar. If it already carries email (MX), domain verification
                            (TXT), or another website, tell us before you switch so we can
                            recreate those records first — otherwise they stop resolving the
                            moment the change propagates. Allow a few hours for propagation, and
                            10–30 minutes after that for the certificate.
                        </>
                    }
                />
            </Card>
        </Space>
    );
}

export default NodeDns;
